import React, { useCallback, useMemo } from 'react';
import { CategoryConfigurationDefinitionResponse } from '@ydistri/api-sdk';
import ConfigurationEntityListRow from './ConfigurationEntityListRow';
import useCategoryId from '../../../../../hooks/useCategoryId';
import { useCategoryConfiguration } from '../../../../../hooks/useCategoryConfiguration';
import { iterableSkuTypes } from '../../../../../helpers/types/iterableEnums';
import { parseEntityListConfigs } from './entityListLib';
import ConfigurationEntityListMainRow from './ConfigurationEntityListMainRow';
import { highlightedConfigurationFieldTypes } from '../segmentPotentialDefinitionLib';
import { useIsEntityListSectionDisabled } from './useIsEntityListSectionDisabled';
import useSpecialOptionsBasedOnConfiguration from './useSpecialOptionsBasedOnConfiguration';
import { SpecialConfigurationSettings } from './SpecialOptionDeleteButton';
import ConfigurationRow from '../ConfigurationRow';
import { isRootCategory } from '../../../../../lib/categories/categoriesLib';
import { useEntityListSectionModifier } from './useEntityListSectionModifier';
import { useTemplateOrCalculation } from '../../../../../hooks/useTemplateOrCalculation';
import SubconfigurationRow from '../components/components';
import useConfiguredEntityLists from './useConfiguredEntityLists';

interface ConfigurationEntityListsProps {
  definition: CategoryConfigurationDefinitionResponse;
  forcedTitle?: string;
  forcedCategoryId?: number;
  forcedEntityListId?: number;
  fullyFeatured?: boolean;
  /** adds additional (special) configurations and header for subconfigurations */
  mainConfigurationView?: boolean;
}

const ConfigurationEntityLists: React.FC<ConfigurationEntityListsProps> = ({
  definition,
  forcedTitle,
  forcedCategoryId,
  forcedEntityListId,
  fullyFeatured = true,
  mainConfigurationView = true,
}) => {
  const selectedCategoryId = useCategoryId();
  const categoryId = forcedCategoryId ?? selectedCategoryId;
  const templateOrCalculation = useTemplateOrCalculation();
  const { categoryConfigurationData: configurations } = useCategoryConfiguration(categoryId);
  const [enableEntityListSection, disableEntityListSection] = useEntityListSectionModifier();

  const getConfiguredEntityLists = useConfiguredEntityLists(categoryId);

  const entityListSectionDisabled = useIsEntityListSectionDisabled(
    categoryId,
    definition.fieldType,
  );

  const specialOptionsBasedOnConfiguration = useSpecialOptionsBasedOnConfiguration(
    definition.fieldType,
  );

  const specialConfigurationSettings: {
    additionalConfigurations: SpecialConfigurationSettings;
    specialSelectOptions: SpecialConfigurationSettings;
  } = useMemo(
    () => ({
      additionalConfigurations: { categoryId, displayDeleteButton: 'gap', italic: true },
      specialSelectOptions: { categoryId, displayDeleteButton: true, italic: true },
    }),
    [categoryId],
  );

  const configuredEntityLists = getConfiguredEntityLists(definition.fieldType);

  const highlighted = highlightedConfigurationFieldTypes[definition.fieldType];
  const fieldsDisabled = entityListSectionDisabled || !isRootCategory(categoryId);

  //we need to disable the whole section so no changes are possible during update
  //special fields have their own fieldType, but we treat them as part of the section
  //therefore we need to use the fieldType from props
  const beforeMainRowFieldSave = useCallback(
    (categoryId: number) => {
      disableEntityListSection(templateOrCalculation.id, categoryId, definition.fieldType);
    },
    [definition.fieldType, disableEntityListSection, templateOrCalculation.id],
  );

  //we need to enable the whole section so changes are again possible
  const afterMainRowFieldSave = useCallback(
    (categoryId: number) => {
      enableEntityListSection(templateOrCalculation.id, categoryId, definition.fieldType);
    },
    [definition.fieldType, enableEntityListSection, templateOrCalculation.id],
  );

  return (
    <>
      {fullyFeatured && (
        <ConfigurationEntityListMainRow
          definition={definition}
          forcedTitle={forcedTitle}
          forcedCategoryId={forcedCategoryId}
          fullyFeatured={fullyFeatured}
          disabled={entityListSectionDisabled}
        />
      )}
      {configuredEntityLists.map(entityList => {
        //TODO fullyFeatured - if not, display only lists with exceptions
        let displayRow = fullyFeatured;
        if (!fullyFeatured) {
          const cfg = configurations?.[definition.fieldType];
          if (cfg) {
            displayRow = !!iterableSkuTypes.find(skuType => {
              const exceptions = cfg[skuType]?.exceptions;
              if (exceptions) {
                return !!exceptions.find(
                  exception =>
                    exception.categoryId === categoryId &&
                    !!parseEntityListConfigs(exception.value).find(
                      x => x.EntityListId === entityList.entityListId,
                    ),
                );
              } else {
                return false;
              }
            });
          }

          if (forcedEntityListId && displayRow) {
            displayRow = entityList.entityListId === forcedEntityListId;
          }
        }

        if (!displayRow) return null;

        return (
          <ConfigurationEntityListRow
            definition={definition}
            entityList={entityList}
            key={entityList.entityListId}
            forcedTitle={forcedTitle}
            forcedCategoryId={forcedCategoryId}
            fullyFeatured={fullyFeatured}
            disabled={entityListSectionDisabled}
          />
        );
      })}
      {mainConfigurationView && (
        <>
          {specialOptionsBasedOnConfiguration.definitions.map(definition => (
            <ConfigurationRow
              specialConfigurationSettings={specialConfigurationSettings.specialSelectOptions}
              definition={definition}
              key={definition.id}
              titleDisabled={fieldsDisabled}
              controlsDisabled={entityListSectionDisabled}
              onBeforeSave={beforeMainRowFieldSave}
              onAfterSave={afterMainRowFieldSave}
            />
          ))}
          {specialOptionsBasedOnConfiguration.additionalConfigurations.map(definition => (
            <ConfigurationRow
              specialConfigurationSettings={specialConfigurationSettings.additionalConfigurations}
              definition={definition}
              key={definition.id}
              titleDisabled={fieldsDisabled}
              controlsDisabled={entityListSectionDisabled}
              onBeforeSave={beforeMainRowFieldSave}
              onAfterSave={afterMainRowFieldSave}
            />
          ))}
          {highlighted && <SubconfigurationRow start />}
        </>
      )}
    </>
  );
};

export default ConfigurationEntityLists;
